import * as Sentry from "@sentry/react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { AddressView } from "~/components/ui/address-view";
import { ArrowDownIcon } from "~/components/ui/icons/arrow-down";
import { CopyIcon } from "~/components/ui/icons/copy";
import { toast } from "~/components/ui/toast";
import {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipTrigger,
} from "~/components/ui/tooltip";
import type { OrderQuery } from "~/graphql/graphql-new.gen";
import { useCopyToClipboard } from "~/hooks/use-copy-to-clipboard";
import { track } from "~/libs/analytics";
import {
  getFormattedAddress,
  recipientToAddress,
  recipientToShopifyAddress,
} from "~/utils/address";

type ModalOrderRecipientAddressProps = {
  order: OrderQuery["order"] | undefined;
};

export const OrderRecipientAddress: React.FC<
  ModalOrderRecipientAddressProps
> = ({ order }: ModalOrderRecipientAddressProps) => {
  const { t, i18n } = useTranslation();

  const [isPreviousAddressMenuOpen, setIsPreviousAddressMenuOpen] =
    useState(false);

  const [addressClipboardState, copyAddressToClipboard] = useCopyToClipboard();

  useEffect(() => {
    const { value, error } = addressClipboardState;
    if (value) {
      toast.success(t("order.copy_address_feedback"));
    }
    if (error) {
      Sentry.captureException(error);
    }
  }, [addressClipboardState, t]);

  const handlePreviousAddressMenuClick = () =>
    setIsPreviousAddressMenuOpen(!isPreviousAddressMenuOpen);

  const onCopyAddress = async (recipient: OrderQuery["order"]["recipient"]) => {
    let stringToCopy = "";
    await getFormattedAddress(
      recipientToShopifyAddress(recipient),
      i18n.language === "fr" ? "FR" : "EN",
    ).then((address) => {
      address.forEach((row) => (stringToCopy += `${row}\n`));
    });
    copyAddressToClipboard(stringToCopy);
    track("CopyToClipboard", {
      label: "shipping_address",
    });
  };
  const onCopyPreviousAddress = async (
    recipient: OrderQuery["order"]["recipient"]["previous"],
  ) => {
    if (!recipient) return;

    let stringToCopy = "";
    await getFormattedAddress(
      recipientToShopifyAddress(recipient),
      i18n.language === "fr" ? "FR" : "EN",
    ).then((address) => {
      address.forEach((row) => (stringToCopy += `${row}\n`));
    });
    copyAddressToClipboard(stringToCopy);
    track("CopyToClipboard", {
      label: "previous_shipping_address",
    });
  };

  if (!order?.recipient) return null;

  return (
    <section className="space-y-2">
      <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500">
        {t("order.delivery_address")}
      </h4>
      <div className="flex items-start justify-between">
        <AddressView
          label={t("address.label")}
          address={recipientToAddress(order.recipient)}
          locale={i18n.language === "fr" ? "FR" : "EN"}
          size="medium"
        />
        <Tooltip>
          <TooltipTrigger
            onClick={() => onCopyAddress(order.recipient)}
            type="button"
            className="rounded pt-1 transition-colors hover:text-gray-700 focus-visible:ring-2 focus-visible:ring-gray-900/30"
          >
            <CopyIcon className="size-4" />
          </TooltipTrigger>
          <TooltipContent side="left">
            <div className="px-1.5 py-3">{t("order.copy_address_tooltip")}</div>
            <TooltipArrow />
          </TooltipContent>
        </Tooltip>
      </div>

      {order.recipient.previous ? (
        <div className="mb-4 mt-2 flex items-start justify-between">
          <div className="flex flex-col">
            <div
              className="flex cursor-pointer items-center"
              onClick={handlePreviousAddressMenuClick}
            >
              <div className="mr-1 text-xs font-semibold text-gray-700">
                {t("order.previous_address")}
              </div>
              <ArrowDownIcon
                className={`duration-[250ms] size-4 text-gray-700 transition-transform ease-in ${
                  isPreviousAddressMenuOpen ? "rotate-180" : "rotate-0"
                }`}
              />
            </div>
            {isPreviousAddressMenuOpen ? (
              <div className="pt-1">
                <AddressView
                  label={t("address.label")}
                  address={recipientToAddress(order.recipient.previous)}
                  locale={i18n.language === "fr" ? "FR" : "EN"}
                  size="small"
                />
              </div>
            ) : (
              ""
            )}
          </div>
          {isPreviousAddressMenuOpen ? (
            <Tooltip>
              <TooltipTrigger
                onClick={() => onCopyPreviousAddress(order.recipient.previous)}
                type="button"
                className="mt-1 rounded transition-colors hover:text-gray-700 focus-visible:ring-2 focus-visible:ring-gray-900/30"
              >
                <CopyIcon className="size-4" />
              </TooltipTrigger>
              <TooltipContent side="left">
                <div className="px-1.5 py-3">
                  {t("order.copy_address_tooltip")}
                </div>
                <TooltipArrow />
              </TooltipContent>
            </Tooltip>
          ) : (
            ""
          )}
        </div>
      ) : null}
    </section>
  );
};
